import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react'
import cx from 'classnames'

import { AdoptionComponent } from '../../constants/AdoptionComponent'
import { useDsAdoptionAttribute } from '../../utils/dsAdoption'

import { TabsProps } from './interfaces'
import { Tab } from './Tab'
import { findActiveTab } from './utils/findActiveTab'

import cm from './Tabs.module.css'

/**
 * Tabs allow users to switch between different views or sections within the same context by clicking on tabs. Each tab can display a label, an optional icon, and can be dynamically enabled or disabled.
 *
 * [UI Guidelines](https://www.notion.so/manychat/Tabs-215b12e9aa1a802fbf68cd3aee4031cd)
 *
 * [Figma](https://www.figma.com/design/Xf38PdCKeISWnBoh0pLFIIWf/%F0%9F%A7%B1-MC-Desktop-Components?node-id=22698-81027&m=dev)
 */
export const Tabs = <T,>({
  onChange,
  options,
  value,
  className,
  ariaLabel,
  ariaControls,
}: TabsProps<T>) => {
  const [indicatorPosition, setIndicatorPosition] = useState({ left: 0, width: 0 })
  const [selectedTab, setSelectedTab] = useState(0)
  const ref = useRef<HTMLUListElement | null>(null)
  const adoptionAttribute = useDsAdoptionAttribute(AdoptionComponent.TABS)

  const updateIndicatorPosition = useCallback(() => {
    if (ref.current) {
      const activeTab = Array.from(ref.current.children).find(
        (tab) =>
          (tab as HTMLButtonElement).dataset.active === 'active' &&
          (tab as HTMLButtonElement).dataset.disabled !== 'true',
      ) as HTMLButtonElement | undefined

      if (activeTab) {
        const { offsetLeft: left, offsetWidth: width } = activeTab
        if (left !== indicatorPosition.left || width !== indicatorPosition.width) {
          setIndicatorPosition({ left, width })
        }
      }
    }
  }, [indicatorPosition])

  const handleTabClick = useCallback(
    (value: T, index: number) => {
      if (value && index !== undefined && onChange) {
        onChange(value, index)
        setSelectedTab(Number(index))
      }
    },
    [onChange],
  )

  const handleNextTab = (nextIndex: number) => {
    const totalTabs = options.length
    const tabToSelect = (nextIndex + totalTabs) % totalTabs
    setSelectedTab(tabToSelect)
    ref.current?.children[tabToSelect]?.querySelector('button')?.focus()
    onChange?.(options[tabToSelect].value)
  }

  const handleKeyPress = (event: React.KeyboardEvent<HTMLUListElement>) => {
    if (event.key === 'ArrowRight') {
      event.preventDefault()
      const nextTab = findActiveTab(options, selectedTab + 1, 1)
      handleNextTab(nextTab)
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault()
      const previousTab = findActiveTab(options, selectedTab - 1, -1)
      handleNextTab(previousTab)
    }
    if (event.key === 'Home') {
      event.preventDefault()
      const firstTab = findActiveTab(options, 0, 1)
      handleNextTab(firstTab)
    }
    if (event.key === 'End') {
      event.preventDefault()
      const lastTab = findActiveTab(options, options.length - 1, -1)
      handleNextTab(lastTab)
    }
  }

  useEffect(() => {
    updateIndicatorPosition()
  }, [options, value, updateIndicatorPosition])

  const renderedTabs = useMemo(
    () =>
      options.map((tabOption, tabIndex) => (
        <Tab
          key={tabOption.id ?? `${tabOption.value}`}
          options={options}
          value={value}
          onClick={handleTabClick}
          tabOption={tabOption}
          tabIndex={tabIndex}
          ariaControls={ariaControls}
        />
      )),
    [options, value, handleTabClick, ariaControls],
  )

  const indicatorStyle = {
    left: indicatorPosition.left,
    width: indicatorPosition.width,
  }

  return (
    <div className={cx(cm.tabsContainer, className)} {...adoptionAttribute}>
      <div className={cx(cm.tabsWrapper, 'notranslate')}>
        <ul
          role="tablist"
          className={cm.tabs}
          ref={ref}
          aria-label={ariaLabel}
          onKeyDown={handleKeyPress}
        >
          {renderedTabs}
        </ul>
        <div className={cm.indicator}>
          <div style={indicatorStyle} />
        </div>
        <div className={cm.separator} />
      </div>
    </div>
  )
}
